import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Users, Car, Clock, ChevronDown, Plus, MapPin, Calendar, AlertCircle, Shield, Phone, Mail, ClipboardList, Package2 } from 'lucide-react';
import { Customer, CustomerVehicle, getCustomer } from '../lib/customers';
import { useBusiness } from '../contexts/BusinessContext';
import { usePermissions } from '../contexts/PermissionContext';
import CreateScheduleEditor from './booking/CreateScheduleEditor';
import { createBooking } from '../lib/bookings';
import { createStockTransaction } from '../lib/stockItems';
import SelectResourceModal from './manage/SelectResourceModal';
import EditVehicleModal from './booking/EditVehicleModal';
import EditCustomerModal from './manage/EditCustomerModal';
import { Service, ServiceAddon, getServiceAddons } from '../lib/services';
import SelectCustomerModal from './manage/SelectCustomerModal';
import EditServiceModal from './booking/EditServiceModal';
import CreateVehicleModal from './CreateVehicleModal';
import SelectVehicleModal from './manage/SelectVehicleModal';
import SelectServiceModal from './manage/SelectServiceModal';
import SelectInventoryItemModal from './manage/SelectInventoryItemModal';
import RichTextDescription from './RichTextDescription';
import { supabase } from '../lib/supabase';
import SelectServiceAddonsModal from './manage/SelectServiceAddonsModal';
import { ProductPhotoService } from '../services/productPhotoService';
import { executeScheduledTriggers } from '../services/scheduledTriggerProcessor';
import { triggerBookingAutomations } from '../lib/automationsV2';
import { Booking } from '../lib/bookings';
import AddressAutocomplete from './AddressAutocomplete';
import { CustomerVehicle as CustomerVehicleType } from '../types/customers';
// Removed all persistence systems
import ColorPickerModal from './ColorPickerModal';
import CreateColorPresetModal from './CreateColorPresetModal';
import { getColorPresets, createColorPreset, deleteColorPreset, ColorPreset } from '../lib/color-presets';
import { CustomerSourcesService, CustomerSource } from '../lib/customerSources';
import SimpleWorkflowModal from './SimpleWorkflowModal';
import { getResources } from '../lib/resources';

// Extend Service interface to include tax_rate and workflow data
interface ExtendedService extends Service {
  tax_rate?: number;
  tax_exempt?: boolean;
  instanceId?: string; // Unique identifier for this service instance
  workflowData?: Array<{
    workflow_step_library_id?: string;
    title: string;
    description?: string;
    order_index: number;
    assigned_resources: Array<{
      id: string;
      name: string;
      avatar_url?: string;
    }>;
  }>;
}

// Service selection with unique instance ID
interface ServiceSelection {
  instanceId: string;
  vehicle: CustomerVehicle;
  service: ExtendedService;
  priceOption: any;
}

interface ProductSelection {
  instanceId: string;
  vehicle: CustomerVehicle;
  product: {
    id: string;
    name: string;
    acquisition_cost: number;
    price: number;
    quantity_available: number;
    category_name: string;
    quantity: number; // Quantity selected for this booking
  };
}

interface CreateBookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  initialDate?: Date | null;
  initialProposal?: any; // Proposal data to pre-populate the form
  // Warranty-related props
  isWarrantyBooking?: boolean;
  originalBookingId?: string;
  warrantyCustomer?: Customer | null;
  warrantyVehicle?: CustomerVehicle | null;
}

interface SavedColor {
  id: string;
  name: string;
  color: string;
  secondary_color?: string;
}

export default function CreateBookingModal({
  isOpen,
  onClose,
  onSuccess,
  initialDate,
  initialProposal,
  isWarrantyBooking = false,
  originalBookingId,
  warrantyCustomer,
  warrantyVehicle
}: CreateBookingModalProps) {
  const { selectedBusiness } = useBusiness();
  const { currentResource } = usePermissions();
  const [isAnimating, setIsAnimating] = useState(false);
  const [title, setTitle] = useState('Booking');
  const [isLocationBooking, setIsLocationBooking] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [selectedServices, setSelectedServices] = useState<ServiceSelection[]>([]);
  const [currentVehicle, setCurrentVehicle] = useState<CustomerVehicle | null>(null);
  const [isSelectCustomerModalOpen, setIsSelectCustomerModalOpen] = useState(false);
  const [isSelectVehicleModalOpen, setIsSelectVehicleModalOpen] = useState(false);
  const [isCreateVehicleModalOpen, setIsCreateVehicleModalOpen] = useState(false);
  const [isEditCustomerModalOpen, setIsEditCustomerModalOpen] = useState(false);
  const [isEditServiceModalOpen, setIsEditServiceModalOpen] = useState(false);
  const [selectedServiceToEdit, setSelectedServiceToEdit] = useState<any>(null);
  const [isEditVehicleModalOpen, setIsEditVehicleModalOpen] = useState(false);
  const [selectedVehicleToEdit, setSelectedVehicleToEdit] = useState<CustomerVehicle | null>(null);
  const [isSelectServiceModalOpen, setIsSelectServiceModalOpen] = useState(false);
  const [isSelectInventoryItemModalOpen, setIsSelectInventoryItemModalOpen] = useState(false);
  const [isSelectResourceModalOpen, setIsSelectResourceModalOpen] = useState(false);
  const [selectedResources, setSelectedResources] = useState<any[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<ProductSelection[]>([]);
  const [productPhotos, setProductPhotos] = useState<Record<string, string>>({});
  const [scheduleData, setScheduleData] = useState({
    dropOffDate: '',
    dropOffTime: '',
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: '',
    customer_notes: ''
  });
  const [bookingColor, setBookingColor] = useState('#3b82f6'); // Default blue
  const [bookingSecondaryColor, setBookingSecondaryColor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSelectAddonsModalOpen, setIsSelectAddonsModalOpen] = useState(false);
  const [currentService, setCurrentService] = useState<ExtendedService | null>(null);
  const [selectedServiceAddons, setSelectedServiceAddons] = useState<Record<string, ServiceAddon[]>>({});
  const [isColorPickerModalOpen, setIsColorPickerModalOpen] = useState(false);
  const [isCreateColorPresetModalOpen, setIsCreateColorPresetModalOpen] = useState(false);
  const [colorPresets, setColorPresets] = useState<ColorPreset[]>([]);
  const [selectedColorMethod, setSelectedColorMethod] = useState<'custom' | string>('custom'); // 'custom' or preset id
  
  // Customer Source state
  const [customerSources, setCustomerSources] = useState<CustomerSource[]>([]);
  const [selectedCustomerSource, setSelectedCustomerSource] = useState<CustomerSource | null>(null);
  const [isCustomerSourceDropdownOpen, setIsCustomerSourceDropdownOpen] = useState(false);
  const [isCreateSourceModalOpen, setIsCreateSourceModalOpen] = useState(false);
  const [newSourceName, setNewSourceName] = useState('');
  const [createSourceLoading, setCreateSourceLoading] = useState(false);
  
  // Simple workflow state
  const [isSimpleWorkflowModalOpen, setIsSimpleWorkflowModalOpen] = useState(false);
  const [currentServiceForWorkflow, setCurrentServiceForWorkflow] = useState<ExtendedService | null>(null);
  
  // Customer notes expansion state
  const [isCustomerNotesExpanded, setIsCustomerNotesExpanded] = useState(false);
  
  // Resources expansion state
  const [isResourcesExpanded, setIsResourcesExpanded] = useState(false);
  
  // Track which services have workflow templates and their assignment status
  const [servicesWithWorkflows, setServicesWithWorkflows] = useState<Set<string>>(new Set());
  const [servicesWithAssignedWorkflows, setServicesWithAssignedWorkflows] = useState<Set<string>>(new Set());
  
  // Ref for customer source dropdown positioning
  const customerSourceButtonRef = useRef<HTMLButtonElement>(null);
  
  // Warranty booking state
  const [isWarrantyMode, setIsWarrantyMode] = useState(isWarrantyBooking);
  const [originalBookingNumber, setOriginalBookingNumber] = useState<string>('Unknown');
  
  // Removed all persistence systems
  // No need for duplicate modal persistence systems

  // Helper function to save current form state
  const saveBookingProgress = (overrides = {}) => {
    const formData = {
      title,
      isLocationBooking,
      selectedCustomer,
      currentVehicle,
      selectedServices,
      // scheduleData deliberately excluded - we don't want to persist schedule across sessions
      bookingColor,
      bookingSecondaryColor,
      selectedServiceAddons,
      selectedResources,
      ...overrides // Allow overriding specific values for immediate saves
    };
    console.log('💾 Saving progress with data:', Object.keys(formData), formData.selectedCustomer ? `Customer: ${formData.selectedCustomer.first_name} ${formData.selectedCustomer.last_name}` : 'No customer');
    // Note: Persistence now handled centrally by usePageLifecycle
  };

  // Function to load proposal data into the form
  const loadProposalData = async () => {
    if (!initialProposal) return;
    
    try {
      console.log('Loading proposal data:', initialProposal);
      
      // Set the title from the proposal
      setTitle(initialProposal.title || 'Booking');
      
      // Load customer data and their vehicles
      let customerVehicles: CustomerVehicle[] = [];
      if (initialProposal.customer_id) {
        const customerData = await getCustomer(initialProposal.customer_id);
        setSelectedCustomer(customerData);
        
        // Get all vehicles for this customer
        if (supabase) {
          const { data: vehicles, error: vehicleError } = await supabase
            .from('customer_vehicles')
            .select('*')
            .eq('customer_id', initialProposal.customer_id);
          
          if (!vehicleError && vehicles) {
            customerVehicles = vehicles;
            console.log('Loaded customer vehicles:', customerVehicles);
          }
        }
      }
      
      // Get the approved offer
      const approvedOffer = initialProposal.offers?.find((o: any) => o.status === 'approved');
      console.log('Approved offer:', approvedOffer);
      
      if (approvedOffer && approvedOffer.services) {
        // Filter for selected services
        const selectedProposalServices = approvedOffer.services.filter((service: any) => {
          const isSelectable = service.isSelectable || service.is_selectable;
          const isSelected = service.is_selected;
          
          // Include if not selectable OR (selectable AND selected)
          return !isSelectable || (isSelectable && isSelected);
        });
        
        console.log('Selected proposal services:', selectedProposalServices);
        
        // Convert proposal services to booking services format
        const bookingServices = [];
        for (const proposalService of selectedProposalServices) {
          // Find the vehicle from the customer's vehicles
          const vehicleData = customerVehicles.find(v => v.id === proposalService.vehicle_id);
          
          console.log(`Looking for vehicle ${proposalService.vehicle_id}, found:`, vehicleData);
          
          if (vehicleData && supabase) {
            // Get the actual service data from the database
            const { data: serviceData, error: serviceError } = await supabase
              .from('services')
              .select('*')
              .eq('id', proposalService.service_id)
              .single();
            
            if (!serviceError && serviceData) {
              // Create service object matching the expected format
              const service: ExtendedService = {
                ...serviceData,
                tax_rate: proposalService.tax_rate,
                tax_exempt: proposalService.tax_exempt,
                price_options: [{
                  id: proposalService.service_id,
                  service_id: proposalService.service_id,
                  name: 'Standard',
                  price: proposalService.price,
                  duration_minutes: proposalService.duration_minutes
                }]
              };
              
              // Create price option object
              const priceOption = {
                id: proposalService.service_id,
                service_id: proposalService.service_id,
                name: 'Standard',
                price: proposalService.price,
                duration_minutes: proposalService.duration_minutes
              };
              
              bookingServices.push({
                instanceId: `${service.id}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                vehicle: vehicleData,
                service,
                priceOption
              });
            }
          } else {
            // If we can't find the vehicle, create a minimal vehicle object
            // This handles cases where vehicle data might be incomplete
            const minimalVehicle: CustomerVehicle = {
              id: proposalService.vehicle_id,
              customer_id: initialProposal.customer_id,
              make: proposalService.vehicle_make || 'Unknown',
              model: proposalService.vehicle_model || 'Vehicle',
              year: new Date().getFullYear(),
              color: '',
              license_plate: '',
              vin: '',
              mileage: 0,
              notes: '',
              is_primary: false,
              last_service: null,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            };
            
            // Get the service data
            if (supabase) {
              const { data: serviceData, error: serviceError } = await supabase
                .from('services')
                .select('*')
                .eq('id', proposalService.service_id)
                .single();
              
              if (!serviceError && serviceData) {
                const service: ExtendedService = {
                  ...serviceData,
                  tax_rate: proposalService.tax_rate,
                  tax_exempt: proposalService.tax_exempt,
                  price_options: [{
                    id: proposalService.service_id,
                    service_id: proposalService.service_id,
                    name: 'Standard',
                    price: proposalService.price,
                    duration_minutes: proposalService.duration_minutes
                  }]
                };
                
                const priceOption = {
                  id: proposalService.service_id,
                  service_id: proposalService.service_id,
                  name: 'Standard',
                  price: proposalService.price,
                  duration_minutes: proposalService.duration_minutes
                };
                
                bookingServices.push({
                  instanceId: `${service.id}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                  vehicle: minimalVehicle,
                  service,
                  priceOption
                });
              }
            }
          }
        }
        
        console.log('Final booking services:', bookingServices);
        setSelectedServices(bookingServices);
        
        // Set the first vehicle as current if available
        if (bookingServices.length > 0) {
          setCurrentVehicle(bookingServices[0].vehicle);
        }
      }

      // Handle products from the approved offer
      if (approvedOffer && approvedOffer.products) {
        console.log('Loading proposal products:', approvedOffer.products);
        
        // Convert proposal products to booking products format
        const bookingProducts = [];
        for (const proposalProduct of approvedOffer.products) {
          // Find the vehicle from the customer's vehicles
          const vehicleData = customerVehicles.find(v => v.id === proposalProduct.vehicle_id);
          
          console.log(`Looking for vehicle ${proposalProduct.vehicle_id} for product ${proposalProduct.name}, found:`, vehicleData);
          
          if (vehicleData && supabase) {
            // Get the actual stock item data from the database
            const { data: stockItemData, error: stockError } = await supabase
              .from('stock_items')
              .select('*')
              .eq('id', proposalProduct.stock_item_id)
              .single();
            
            if (!stockError && stockItemData) {
              // Create product object matching the expected format
              const product = {
                id: stockItemData.id,
                name: stockItemData.name,
                price: proposalProduct.price, // Use proposal price, not stock price
                quantity: proposalProduct.quantity,
                category_name: proposalProduct.category_name,
                acquisition_cost: stockItemData.acquisition_cost,
                quantity_available: stockItemData.quantity
              };
              
              bookingProducts.push({
                instanceId: `${product.id}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                vehicle: vehicleData,
                product
              });
            }
          } else if (proposalProduct.vehicle_id) {
            // If we can't find the vehicle, create a minimal vehicle object
            const minimalVehicle: CustomerVehicle = {
              id: proposalProduct.vehicle_id,
              customer_id: initialProposal.customer_id,
              make: 'Unknown',
              model: 'Vehicle',
              year: 2020, // Default year
              color: null,
              license_plate: null,
              vin: null,
              mileage: null,
              notes: null,
              is_primary: false,
              last_service: null,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            };
            
            // Create product object with available data
            const product = {
              id: proposalProduct.stock_item_id,
              name: proposalProduct.name,
              price: proposalProduct.price,
              quantity: proposalProduct.quantity,
              category_name: proposalProduct.category_name,
              acquisition_cost: 0, // Default since we don't have stock data
              quantity_available: proposalProduct.quantity // Assume available
            };
            
            bookingProducts.push({
              instanceId: `${product.id}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
              vehicle: minimalVehicle,
              product
            });
          }
        }
        
        console.log('Final booking products:', bookingProducts);
        setSelectedProducts(bookingProducts);
        
        // Load product photos for the selected products
        if (bookingProducts.length > 0) {
          try {
            const stockItemIds = bookingProducts.map(p => p.product.id);
            const photoMap = await ProductPhotoService.getPhotosByStockItemIds(stockItemIds);
            
            // Convert to the format expected by the component
            const photoUrls: Record<string, string> = {};
            if (photoMap && typeof photoMap === 'object') {
              Object.entries(photoMap).forEach(([itemId, photo]) => {
                if (photo && typeof photo === 'object' && 'photo_url' in photo) {
                  photoUrls[itemId] = (photo as any).photo_url;
                }
              });
            }
            setProductPhotos(photoUrls);
          } catch (photoError) {
            console.error('Error loading product photos:', photoError);
            setProductPhotos({});
          }
        }
      }
    } catch (error) {
      console.error('Error loading proposal data:', error);
    }
  };

  // Load saved data when modal opens
  useEffect(() => {
    if (isOpen) {
      console.log('📥 Loading CreateBookingModal progress...');
      // Note: Persistence now handled centrally by usePageLifecycle
      console.log('📥 Progress loading handled by centralized system');
      
      // If we have a proposal, load its data
      if (initialProposal) {
        loadProposalData();
      }
    }
  }, [isOpen, initialProposal]);

  // Generate time options in 30 minute increments
  const timeOptions = Array.from({ length: 48 }, (_, i) => {
    const hour = Math.floor(i / 2);
    const minute = i % 2 === 0 ? '00' : '30';
    const ampm = hour >= 12 ? 'pm' : 'am';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return {
      value: `${hour.toString().padStart(2, '0')}:${minute}`,
      label: `${displayHour}:${minute} ${ampm}`
    };
  });

  // Calculate total duration in minutes
  const totalDurationMinutes = selectedServices.reduce((total, service) => {
    // Add service duration
    let duration = total + service.priceOption.duration_minutes;
    // Add addon durations for this service
    const addons = selectedServiceAddons[service.service.id] || [];
    duration += addons.reduce((addonSum, addon) => addonSum + addon.duration_minutes, 0);
    return duration;
  }, 0);

  React.useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
    } else {
      // Reset state when modal closes to prevent state persistence issues
      setTitle('Booking');
      setSelectedCustomer(null);
      setSelectedServices([]);
      setSelectedProducts([]); // Clear products to prevent cross-booking contamination
      setCurrentVehicle(null);
      setScheduleData({
        dropOffDate: '',
        dropOffTime: '',
        startDate: '',
        startTime: '',
        endDate: '',
        endTime: '',
        customer_notes: ''
      });
      setBookingColor('#3b82f6');
      setBookingSecondaryColor(null);
      setSelectedResources([]);
      setSelectedServiceAddons({});
      setProductPhotos({});
      setError(null);
      setSelectedCustomerSource(null);
      
      console.log('🧹 CreateBookingModal state reset - cleared selectedProducts and other state');
    }
  }, [isOpen]);

  useEffect(() => {
    if (selectedBusiness?.id) {
      // Load color presets from database
      loadColorPresets();
    }
  }, [selectedBusiness?.id]);

  const loadColorPresets = async () => {
    if (!selectedBusiness?.id) return;
    
    try {
      const presets = await getColorPresets(selectedBusiness.id);
      setColorPresets(presets);
    } catch (error) {
      console.error('Error loading color presets:', error);
    }
  };

  // Load customer sources when modal opens
  useEffect(() => {
    console.log('🔄 [CreateBookingModal] useEffect for loadCustomerSources triggered:', {
      isOpen,
      selectedBusinessId: selectedBusiness?.id,
      willLoadSources: isOpen && selectedBusiness?.id
    });
    
    if (isOpen && selectedBusiness?.id) {
      loadCustomerSources();
    }
  }, [isOpen, selectedBusiness?.id]);

  // Initialize warranty booking data when modal opens
  useEffect(() => {
    if (isOpen && isWarrantyBooking) {
      // Try to get warranty data from localStorage first
      const storedWarrantyData = localStorage.getItem('warrantyBookingData');
      if (storedWarrantyData) {
        try {
          const warrantyData = JSON.parse(storedWarrantyData);
          const now = Date.now();
          const dataAge = now - warrantyData.timestamp;
          
          // Only use data that's less than 5 minutes old
          if (dataAge < 5 * 60 * 1000 && warrantyData.customer && warrantyData.vehicle) {
            console.log('🛡️ [CreateBookingModal] Loading warranty data from localStorage:', {
              customer: warrantyData.customer.first_name + ' ' + warrantyData.customer.last_name,
              vehicle: `${warrantyData.vehicle.year} ${warrantyData.vehicle.make} ${warrantyData.vehicle.model}`,
              originalBookingId: warrantyData.originalBookingId
            });
            
            // Pre-populate customer and vehicle
            setSelectedCustomer(warrantyData.customer);
            setCurrentVehicle(warrantyData.vehicle);
            
            // Set title to indicate this is a warranty booking
            setTitle('Warranty Booking');
            
                         // Set warranty mode
             setIsWarrantyMode(true);
             
             // Set the original booking number
             setOriginalBookingNumber(warrantyData.originalBookingNumber || 'Unknown');
             
             // Set warranty customer source
             setSelectedCustomerSource({ 
               id: 'warranty', 
               business_id: selectedBusiness?.id || '',
               name: 'Warranty', 
               is_active: true,
               created_at: '',
               updated_at: ''
             });
             
             // Get original booking ID from localStorage (set by App component)
             const storedOriginalBookingId = localStorage.getItem('warrantyOriginalBookingId');
             if (storedOriginalBookingId) {
               console.log('🛡️ [CreateBookingModal] Found original booking ID in localStorage:', storedOriginalBookingId);
               // Store it in the warranty data for later use
               warrantyData.originalBookingId = storedOriginalBookingId;
             }
             
             // Don't clear localStorage yet - we need it for booking creation
             // localStorage.removeItem('warrantyBookingData');
             // localStorage.removeItem('warrantyOriginalBookingId');
             return;
          } else {
            // Data is too old, remove it
            localStorage.removeItem('warrantyBookingData');
          }
        } catch (error) {
          console.error('Error parsing warranty data from localStorage:', error);
          localStorage.removeItem('warrantyBookingData');
        }
      }
      
      // Fallback to props if available
      if (warrantyCustomer && warrantyVehicle) {
        console.log('🛡️ [CreateBookingModal] Initializing warranty booking with props:', {
          customer: warrantyCustomer.first_name + ' ' + warrantyCustomer.last_name,
          vehicle: `${warrantyVehicle.year} ${warrantyVehicle.make} ${warrantyVehicle.model}`,
          originalBookingId
        });
        
        // Pre-populate customer and vehicle
        setSelectedCustomer(warrantyCustomer);
        setCurrentVehicle(warrantyVehicle);
        
        // Set title to indicate this is a warranty booking
        setTitle('Warranty Booking');
        
        // Set warranty mode
        setIsWarrantyMode(true);
        
                 // Set the original booking number from props if available
         if (originalBookingId) {
           setOriginalBookingNumber(originalBookingId.slice(-8));
         }
         
         // Set warranty customer source
         setSelectedCustomerSource({ 
           id: 'warranty', 
           business_id: selectedBusiness?.id || '',
           name: 'Warranty', 
           is_active: true,
           created_at: '',
           updated_at: ''
         });
         
         // Get original booking ID from localStorage (set by App component)
         const storedOriginalBookingId = localStorage.getItem('warrantyOriginalBookingId');
         if (storedOriginalBookingId) {
           console.log('🛡️ [CreateBookingModal] Found original booking ID in localStorage (fallback):', storedOriginalBookingId);
           // Update the originalBookingId prop for later use
           originalBookingId = storedOriginalBookingId;
         }
      }
    }
  }, [isOpen, isWarrantyBooking, warrantyCustomer, warrantyVehicle, originalBookingId]);

  const loadCustomerSources = async () => {
    console.log('🔍 [CreateBookingModal] loadCustomerSources called:', {
      selectedBusinessId: selectedBusiness?.id,
      selectedBusinessName: selectedBusiness?.name,
      hasSupabase: !!supabase
    });
    
    if (!selectedBusiness?.id) {
      console.warn('❌ [CreateBookingModal] No selectedBusiness.id available');
      return;
    }
    
    try {
      console.log('📡 [CreateBookingModal] Calling CustomerSourcesService.getCustomerSources...');
      const sources = await CustomerSourcesService.getCustomerSources(selectedBusiness.id);
      console.log('✅ [CreateBookingModal] Customer sources loaded:', {
        count: sources.length,
        sources: sources.map(s => ({ id: s.id, name: s.name, is_active: s.is_active }))
      });
      setCustomerSources(sources);
    } catch (error) {
      console.error('❌ [CreateBookingModal] Error loading customer sources:', {
        error: error,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
        businessId: selectedBusiness.id,
        stack: error instanceof Error ? error.stack : undefined
      });
    }
  };

  const handleCreateCustomerSource = async () => {
    if (!selectedBusiness?.id || !newSourceName.trim()) return;
    
    try {
      setCreateSourceLoading(true);
      const newSource = await CustomerSourcesService.createCustomerSource({
        business_id: selectedBusiness.id,
        name: newSourceName.trim()
      });
      
      // Add to list and select it
      setCustomerSources(prev => [...prev, newSource].sort((a, b) => a.name.localeCompare(b.name)));
      setSelectedCustomerSource(newSource);
      
      // Close modal and reset
      setIsCreateSourceModalOpen(false);
      setNewSourceName('');
    } catch (error) {
      console.error('Error creating customer source:', error);
      alert(error instanceof Error ? error.message : 'Failed to create customer source');
    } finally {
      setCreateSourceLoading(false);
    }
  };

  // Auto-detect customer source based on repeat status and booking history
  const detectCustomerSource = async (customer: Customer) => {
    if (!selectedBusiness?.id) return;
    
    try {
      // Step 1: Check customer.repeat column first (optimization)
      if (customer.repeat === true) {
        // Customer is already marked as repeat, auto-select repeat source
        setSelectedCustomerSource({ 
          id: 'repeat-customer', 
          business_id: selectedBusiness.id,
          name: 'Repeat Customer', 
          is_active: true,
          created_at: '',
          updated_at: ''
        });
        return;
      }
      
      // Step 2: If customer.repeat = false, count their total bookings (all statuses)
      if (!supabase) return;
      
      const { data: bookingsData, error } = await supabase
        .from('bookings')
        .select('id')
        .eq('customer_id', customer.id)
        .eq('business_id', selectedBusiness.id);
      
      if (error) {
        console.error('Error counting customer bookings:', error);
        return;
      }
      
      const totalBookings = bookingsData?.length || 0;
      
      // If customer has 1+ bookings, this will be their 2nd+ booking (repeat)
      // We also need to update their repeat status in the database
      if (totalBookings >= 1) {
        setSelectedCustomerSource({ 
          id: 'repeat-customer', 
          business_id: selectedBusiness.id,
          name: 'Repeat Customer', 
          is_active: true,
          created_at: '',
          updated_at: ''
        });
        
        // Update customer's repeat column to true since this will be their 2nd+ booking
        try {
          const { error: updateError } = await supabase
            .from('customers')
            .update({ 
              repeat: true,
              updated_at: new Date().toISOString()
            })
            .eq('id', customer.id);
          
          if (updateError) {
            console.error('Error updating customer repeat status:', updateError);
          } else {
            console.log(`✅ Updated customer ${customer.first_name} ${customer.last_name} to repeat status`);
            // Update local customer state
            setSelectedCustomer(prev => prev ? { ...prev, repeat: true } : null);
          }
        } catch (updateError) {
          console.error('Error updating customer repeat status:', updateError);
        }
      } else {
        // Customer has 0 bookings, this will be their 1st (new customer)
        setSelectedCustomerSource(null);
      }
      
    } catch (error) {
      console.error('Error detecting customer source:', error);
    }
  };

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      // Reset all form fields
      setTitle('Booking');
      setSelectedCustomer(null);
      setCurrentVehicle(null);
      setSelectedServices([]);
      setSelectedResources([]);
      setScheduleData({
        dropOffDate: '',
        dropOffTime: '',
        startDate: '',
        startTime: '',
        endDate: '',
        endTime: '',
        customer_notes: ''
      });
      setBookingColor('#3b82f6');
      setBookingSecondaryColor(null);
      setError(null);

      // This is now a location-only booking modal
      setIsLocationBooking(true);
    }
  };

  const handleVehicleCreated = async (vehicle: any) => {
    // Update customer data to get the new vehicle
    const updatedCustomer = await getCustomer(selectedCustomer?.id || '');
    setSelectedCustomer(updatedCustomer);
    
    // Set the new vehicle as current and open service selection
    setCurrentVehicle(vehicle);
    setIsCreateVehicleModalOpen(false);
    setIsSelectServiceModalOpen(true);
    
    // Save progress after vehicle creation
    saveBookingProgress();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError(null);

      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      if (!selectedCustomer || !selectedBusiness || selectedServices.length === 0) {
        throw new Error('Please select a customer, business, and at least one service');
      }

      console.log('🔍 [CreateBookingModal] Form validation - selectedCustomerSource:', selectedCustomerSource);
      console.log('🔍 [CreateBookingModal] Form validation - isWarrantyMode:', isWarrantyMode);
      
      // For warranty bookings, skip customer source validation since it's auto-set
      if (isWarrantyMode) {
        console.log('🛡️ [CreateBookingModal] Warranty mode - skipping customer source validation');
        // Use warranty as the customer source
        const warrantyCustomerSource = { 
          id: 'warranty', 
          business_id: selectedBusiness?.id || '',
          name: 'Warranty', 
          is_active: true,
          created_at: '',
          updated_at: ''
        };
        // Update the state for consistency
        setSelectedCustomerSource(warrantyCustomerSource);
      } else if (!selectedCustomerSource) {
        throw new Error('Please select a customer source');
      }

      // Removed test trigger to prevent duplicate automation executions
      // The real trigger runs after booking creation with actual data

      // Now continue with normal booking creation flow...
      console.log('📝 Proceeding with actual booking creation...');

      // Get total bookings count for customer repeat status determination
      let totalBookings = 0;
      if (selectedCustomer.repeat === false) {
        // Only count if customer is not already marked as repeat
        const { data: bookingsData } = await supabase
          .from('bookings')
          .select('id')
          .eq('customer_id', selectedCustomer.id)
          .eq('business_id', selectedBusiness.id);
        totalBookings = bookingsData?.length || 0;
      }

      // Determine customer repeat status for this booking
      const customerRepeatStatus = selectedCustomer.repeat === true ? 'repeat' : 
        (totalBookings >= 1 ? 'repeat' : 'new');
      
      // Get selected customer source name (or undefined if none selected)
      // For warranty bookings, always use "Warranty" as the source
      const customerSourceName = isWarrantyMode ? 'Warranty' : (selectedCustomerSource ? selectedCustomerSource.name : undefined);

      // Debug logging for warranty booking creation
      if (isWarrantyMode) {
        console.log('🛡️ [CreateBookingModal] About to create warranty booking with data:', {
          is_warranty: isWarrantyMode,
          original_booking_id: (() => {
            if (!isWarrantyMode) return undefined;
            
            console.log('🛡️ [CreateBookingModal] Setting original_booking_id for warranty booking');
            console.log('🛡️ [CreateBookingModal] isWarrantyMode:', isWarrantyMode);
            console.log('🛡️ [CreateBookingModal] originalBookingId prop:', originalBookingId);
            
            // Try to get original booking ID from localStorage first (from warranty data)
            const storedWarrantyData = localStorage.getItem('warrantyBookingData');
            console.log('🛡️ [CreateBookingModal] storedWarrantyData from localStorage:', storedWarrantyData);
            
            if (storedWarrantyData) {
              try {
                const warrantyData = JSON.parse(storedWarrantyData);
                console.log('🛡️ [CreateBookingModal] Parsed warranty data:', warrantyData);
                console.log('🛡️ [CreateBookingModal] originalBookingId from localStorage:', warrantyData.originalBookingId);
                return warrantyData.originalBookingId;
              } catch (error) {
                console.error('Error parsing warranty data for original_booking_id:', error);
              }
            }
            
            // Try to get original booking ID from localStorage (set by App component)
            const storedOriginalBookingId = localStorage.getItem('warrantyOriginalBookingId');
            console.log('🛡️ [CreateBookingModal] storedOriginalBookingId from localStorage:', storedOriginalBookingId);
            
            if (storedOriginalBookingId) {
              console.log('🛡️ [CreateBookingModal] Using originalBookingId from App localStorage:', storedOriginalBookingId);
              return storedOriginalBookingId;
            }
            
            // Fallback to prop
            console.log('🛡️ [CreateBookingModal] Using originalBookingId prop as fallback:', originalBookingId);
            return originalBookingId;
          })()
        });
      }

      // Create the booking first
      const result = await createBooking({
        business_id: selectedBusiness.id,
        customer_id: selectedCustomer.id,
        title: title || `${selectedCustomer.first_name} ${selectedCustomer.last_name}`,
        start_date: scheduleData.startDate,
        start_time: scheduleData.startTime,
        end_date: scheduleData.endDate,
        end_time: scheduleData.endTime,
        drop_off_date: scheduleData.dropOffDate,
        drop_off_time: scheduleData.dropOffTime,
        status: 'scheduled',
        notes: `Created with ${selectedServices.length} services`,
        customer_notes: scheduleData.customer_notes || "",
        color: bookingColor,
        secondary_color: bookingSecondaryColor || undefined,
        customer_repeat_status: customerRepeatStatus,
        source: customerSourceName,
        created_by: currentResource?.id || 'admin',
        // Warranty booking fields
        is_warranty: isWarrantyMode,
        original_booking_id: (() => {
          if (!isWarrantyMode) return undefined;
          
          console.log('🛡️ [CreateBookingModal] Setting original_booking_id for warranty booking');
          console.log('🛡️ [CreateBookingModal] isWarrantyMode:', isWarrantyMode);
          console.log('🛡️ [CreateBookingModal] originalBookingId prop:', originalBookingId);
          
          // Try to get original booking ID from localStorage first (from warranty data)
          const storedWarrantyData = localStorage.getItem('warrantyBookingData');
          console.log('🛡️ [CreateBookingModal] storedWarrantyData from localStorage:', storedWarrantyData);
          
          if (storedWarrantyData) {
            try {
              const warrantyData = JSON.parse(storedWarrantyData);
              console.log('🛡️ [CreateBookingModal] Parsed warranty data:', warrantyData);
              console.log('🛡️ [CreateBookingModal] originalBookingId from localStorage:', warrantyData.originalBookingId);
              return warrantyData.originalBookingId;
            } catch (error) {
              console.error('Error parsing warranty data for original_booking_id:', error);
            }
          }
          
          // Try to get original booking ID from localStorage (set by App component)
          const storedOriginalBookingId = localStorage.getItem('warrantyOriginalBookingId');
          console.log('🛡️ [CreateBookingModal] storedOriginalBookingId from localStorage:', storedOriginalBookingId);
          
          if (storedOriginalBookingId) {
            console.log('🛡️ [CreateBookingModal] Using originalBookingId from App localStorage:', storedOriginalBookingId);
            return storedOriginalBookingId;
          }
          
          // Fallback to prop
          console.log('🛡️ [CreateBookingModal] Using originalBookingId prop as fallback:', originalBookingId);
          return originalBookingId;
        })(),
        // Group services by vehicle
        vehicles: Object.entries(servicesByVehicle).map(([vehicleId, services]) => ({
          vehicle_id: vehicleId,
          services: services.map(service => {
            const addons = selectedServiceAddons[service.service.id] || [];
            const addonTotal = addons.reduce((sum, addon) => sum + addon.price, 0);
            const servicePrice = service.priceOption.price;
            const taxRate = service.service.tax_exempt ? 0 : (service.service.tax_rate || 5);
            const taxableAmount = servicePrice + addonTotal;
            const taxAmount = service.service.tax_exempt ? 0 : Math.round((taxableAmount * taxRate / 100) * 100) / 100;
            
            return {
              service_id: service.service.id,
              name: service.service.name,
              description: service.service.description || '',
              price: servicePrice,
              tax_exempt: service.service.tax_exempt || false,
              tax_rate: taxRate,
              tax_amount: taxAmount,
              total_amount: taxableAmount + taxAmount,
              duration_minutes: service.priceOption.duration_minutes,
              // Add workflow data if this service instance has it assigned
              workflow_steps: (() => {
                const workflowData = service.service.workflowData || null;
                if (workflowData) {
                  console.log(`📋 Saving workflow data for service "${service.service.name}":`, workflowData);
                }
                return workflowData;
              })()
            };
          })
        })),
        // Add selected resources if any
        resources: selectedResources.map(resource => ({
          resource_id: resource.id,
          role: 'technician'
        })),
        // Workflow assignments will be added with JSONB approach
      });

      if (!result || !result.id) throw new Error('No booking data returned');

      // Insert add-ons and workflow data for each service
      for (const serviceData of selectedServices) {
        const addons = selectedServiceAddons[serviceData.service.id] || [];
        const workflowData = serviceData.service.workflowData;
        
        if (addons.length > 0 || workflowData) {
          const { data: serviceResult } = await supabase
            .from('booking_services')
            .select('id')
            .eq('booking_id', result.id)
            .eq('service_id', serviceData.service.id)
            .single();

          if (serviceResult) {
            // Insert add-ons
            if (addons.length > 0) {
              const { error: addonsError } = await supabase
                .from('booking_service_addons')
                .insert(
                  addons.map(addon => ({
                    booking_service_id: serviceResult.id,
                    addon_id: addon.id,
                    price: addon.price
                  }))
                );

              if (addonsError) throw addonsError;
            }

            // Insert workflow data to JSONB column
            if (workflowData && workflowData.length > 0) {
              const { error: workflowError } = await supabase
                .from('booking_services')
                .update({
                  workflow_steps: workflowData
                })
                .eq('id', serviceResult.id);

              if (workflowError) {
                console.error('Error saving workflow data:', workflowError);
                throw workflowError;
              }

              console.log(`Workflow data saved for service ${serviceData.service.name}:`, workflowData);
            }
          }
        }
      }

      // Create stock transactions for selected products (Step 1: Stock reduction only)
      if (selectedProducts.length > 0) {
        console.log(`📦 Creating stock transactions for ${selectedProducts.length} products...`);
        
        try {
          for (const productSelection of selectedProducts) {
            await createStockTransaction({
              item_id: productSelection.product.id,
              type: 'sale',
              quantity: productSelection.product.quantity,
              price: productSelection.product.price,
              notes: `Booking: ${result.id} - ${selectedCustomer.first_name} ${selectedCustomer.last_name}`,
              booking_id: result.id
            });
            
            console.log(`✅ Stock transaction created for ${productSelection.product.name} (qty: ${productSelection.product.quantity})`);
          }
          
          console.log('📦 All stock transactions created successfully');
        } catch (stockError) {
          console.error('❌ Error creating stock transactions:', stockError);
          // Note: We don't throw here to avoid blocking the booking creation
          // The booking is already created, stock transactions are supplementary for Step 1
        }
      }

      // Create product_booking_sales entries for selected products
      if (selectedProducts.length > 0) {
        console.log(`📋 Creating product_booking_sales entries for ${selectedProducts.length} products...`);
        
        try {
          for (const productSelection of selectedProducts) {
            const { data: productSaleEntry, error: productSaleError } = await supabase
              .from('product_booking_sales')
              .insert({
                business_id: selectedBusiness?.id,
                booking_id: result.id,
                stock_item_id: productSelection.product.id,
                product_name: productSelection.product.name,
                quantity: productSelection.product.quantity,
                unit_price: productSelection.product.price,
                total_price: productSelection.product.price * productSelection.product.quantity
              });

            if (productSaleError) {
              console.error(`❌ Error creating product_booking_sales entry for ${productSelection.product.name}:`, productSaleError);
              throw productSaleError;
            }
            
            console.log(`✅ Product booking sale entry created for ${productSelection.product.name} (qty: ${productSelection.product.quantity}, total: $${(productSelection.product.price * productSelection.product.quantity).toFixed(2)})`);
          }
          
          console.log('✅ All product_booking_sales entries created successfully');
        } catch (error) {
          console.error('❌ Error creating product_booking_sales entries:', error);
          throw error;
        }
      }

      // Execute scheduled triggers after booking is created
      if (selectedBusiness?.id) {
        try {
          // Extract customer information
          const customerInfo = {
            name: `${selectedCustomer.first_name} ${selectedCustomer.last_name}`,
            firstName: selectedCustomer.first_name,
            lastName: selectedCustomer.last_name,
            phone: selectedCustomer.phone || undefined,
            email: selectedCustomer.email || undefined,
            booking: {
              dropOffDate: scheduleData.dropOffDate || undefined,
              dropOffTime: scheduleData.dropOffTime || undefined,
              startDate: scheduleData.startDate || undefined,
              startTime: scheduleData.startTime || undefined,
              endDate: scheduleData.endDate || undefined,
              endTime: scheduleData.endTime || undefined
            }
          };

          // This runs in the background and doesn't block UI
          executeScheduledTriggers(selectedBusiness.id, customerInfo)
            .catch(triggerError => console.error('Error executing scheduled triggers:', triggerError));
          
          console.log('Scheduled triggers initiated for new booking');
        } catch (triggerError) {
          console.error('Failed to execute scheduled triggers:', triggerError);
          // We don't throw here to avoid blocking the booking creation flow
        }


      }

      // Removed all persistence systems
      // Note: Persistence now handled centrally by usePageLifecycle

      // Trigger automations for real booking creation
      try {
        console.log('🤖 REAL BOOKING: Triggering booking automations for created event...');
        
        // Build comprehensive booking data for automation
        const realBookingData = {
          booking_id: result.booking_number || `#${result.id.slice(-4)}`, // Use booking number like "#1134"
          title: title || `${selectedCustomer.first_name} ${selectedCustomer.last_name}`,
          grand_total: selectedServices.reduce((total, service) => {
            const addons = selectedServiceAddons[service.service.id] || [];
            const addonTotal = addons.reduce((sum, addon) => sum + addon.price, 0);
            const servicePrice = service.priceOption.price;
            const taxRate = service.service.tax_exempt ? 0 : (service.service.tax_rate || 5);
            const taxableAmount = servicePrice + addonTotal;
            const taxAmount = service.service.tax_exempt ? 0 : Math.round((taxableAmount * taxRate / 100) * 100) / 100;
            return total + taxableAmount + taxAmount;
          }, 0),
          
          // Customer information
          customer: {
            id: selectedCustomer.id,
            name: `${selectedCustomer.first_name} ${selectedCustomer.last_name}`,
            full_name: `${selectedCustomer.first_name} ${selectedCustomer.last_name}`,
            first_name: selectedCustomer.first_name,
            last_name: selectedCustomer.last_name,
            email: selectedCustomer.email,
            phone: selectedCustomer.phone
          },
          
          // Schedule information
          schedule: {
            start_date: scheduleData.startDate,
            start_time: scheduleData.startTime,
            end_date: scheduleData.endDate,
            end_time: scheduleData.endTime,
            drop_off_date: scheduleData.dropOffDate,
            drop_off_time: scheduleData.dropOffTime
          },
          
          // Services information
          services: selectedServices.map(service => ({
            id: service.service.id,
            name: service.service.name,
            price: service.priceOption.price,
            duration_minutes: service.priceOption.duration_minutes,
            vehicle: {
              id: service.vehicle.id,
              make: service.vehicle.make,
              model: service.vehicle.model,
              year: service.vehicle.year,
              color: service.vehicle.color
            },
            addons: selectedServiceAddons[service.service.id] || []
          })),
          
          // Resources assigned
          resources: selectedResources.map(resource => ({
            id: resource.id,
            name: resource.name,
            phone: resource.phone,
            role: 'technician'
          })),
          
          // Vehicle summary for easy access
          vehicles: (() => {
            const vehicles = selectedServices.map(service => ({
              make: service.vehicle.make || '',
              model: service.vehicle.model || '',
              year: service.vehicle.year || 0,
              color: service.vehicle.color || '',
              display: `${service.vehicle.year || ''} ${service.vehicle.make || ''} ${service.vehicle.model || ''}`.trim()
            }));
            
            // Remove duplicates based on make/model/year combination
            const uniqueVehicles = vehicles.filter((vehicle, index, self) => 
              index === self.findIndex(v => v.make === vehicle.make && v.model === vehicle.model && v.year === vehicle.year)
            );
            
            return uniqueVehicles;
          })(),
          
          // Additional booking details
          notes: scheduleData.customer_notes || null,
          color: bookingColor,
          secondary_color: bookingSecondaryColor || null,
          created_at: new Date().toISOString(),
          status: 'scheduled'
        };

        console.log('📋 Real booking data:', realBookingData);
        
        // Trigger automation (non-blocking)
        triggerBookingAutomations(selectedBusiness.id, 'created', realBookingData)
          .then(() => console.log('🤖 REAL BOOKING: Booking automations completed successfully'))
          .catch(automationError => console.error('❌ REAL BOOKING: Error triggering booking automations:', automationError));
          
        console.log('🤖 REAL BOOKING: Booking automation trigger initiated');
      } catch (automationError) {
        console.error('❌ REAL BOOKING: Failed to trigger booking automations:', automationError);
        // We don't throw here to avoid blocking the booking creation flow
      }

      // Update proposal status to 'booked' if this was created from a proposal
      if (initialProposal?.id) {
        try {
          const { error: proposalUpdateError } = await supabase
            .from('proposals')
            .update({ status: 'booked' })
            .eq('id', initialProposal.id);

          if (proposalUpdateError) {
            console.error('❌ Failed to update proposal status to booked:', proposalUpdateError);
          } else {
            console.log('✅ Updated proposal status to booked:', initialProposal.id);
          }
        } catch (error) {
          console.error('❌ Error updating proposal status:', error);
        }
      }

      // Emit custom event to notify other components (like CalendarV2) immediately
      window.dispatchEvent(new CustomEvent('bookingCreated', { 
        detail: { bookingId: result.id, businessId: selectedBusiness.id } 
      }));
      console.log('📅 Dispatched bookingCreated event for immediate calendar update');

      // Clear warranty booking data from localStorage after successful creation
      if (isWarrantyMode) {
        console.log('🛡️ [CreateBookingModal] Clearing warranty data from localStorage after successful booking creation');
        localStorage.removeItem('warrantyBookingData');
        localStorage.removeItem('warrantyOriginalBookingId');
      }

      onSuccess?.();
      onClose();
    } catch (err) {
      console.error('Error creating booking:', err);
      setError(err instanceof Error ? err.message : 'Failed to create booking. Please check all required fields.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddService = async (service: ExtendedService, priceOption: any) => {
    if (!currentVehicle) return;

    // Generate unique instance ID for this service instance
    const instanceId = `${service.id}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    const newService: ServiceSelection = {
      instanceId, // Unique identifier for this service instance
      vehicle: currentVehicle,
      service: {
        ...service,
        tax_rate: service.tax_rate || 5
      },
      priceOption
    };

    // Calculate new services array
    const newServicesArray = [...selectedServices, newService];
    setSelectedServices(newServicesArray);
    
    // Save progress with new services array immediately - bypass timing issues
    saveBookingProgress({ selectedServices: newServicesArray });
    
    // Check if service has a workflow template
    try {
      const { data: serviceWorkflow, error } = await supabase
        .from('service_workflows')
        .select('id, name')
        .eq('service_id', service.id)
        .eq('is_active', true)
        .single();

      if (serviceWorkflow && !error) {
        console.log(`Service "${service.name}" has workflow: ${serviceWorkflow.name}`);
        
        // Track that this service instance has a workflow template
        setServicesWithWorkflows(prev => new Set(prev).add(instanceId));
        
        // Pass the service with instanceId info
        setCurrentServiceForWorkflow({ ...service, instanceId });
        setIsSimpleWorkflowModalOpen(true);
        return; // Exit early - workflow modal will handle next steps
      }
    } catch (error) {
      console.log(`Service "${service.name}" has no workflow template`);
      // Continue to addon check
    }
    
    // Check if service has addons before opening the modal
    try {
      const availableAddons = await getServiceAddons(service.id);
      
      // Only open addons modal if there are actually addons available
      if (availableAddons && availableAddons.length > 0) {
        console.log(`Service "${service.name}" has ${availableAddons.length} addons available - opening addons modal`);
        setCurrentService(service);
        setIsSelectAddonsModalOpen(true);
      } else {
        console.log(`Service "${service.name}" has no addons available - skipping addons modal`);
        // No addons available, just continue without opening the modal
      }
    } catch (error) {
      console.error('Error checking for service addons:', error);
      // On error, don't open the modal to be safe
    }
  };

  const handleAddProduct = async (product: any) => {
    if (!currentVehicle) {
      console.error('No current vehicle selected for product');
      return;
    }

    // Generate unique instance ID for this product instance
    const instanceId = `product_${product.id}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    const newProductSelection: ProductSelection = {
      instanceId,
      vehicle: currentVehicle,
      product: {
        id: product.id,
        name: product.name,
        acquisition_cost: product.acquisition_cost || 0,
        price: product.price || 0,
        quantity_available: product.quantity || 0,
        category_name: product.category_name || 'Uncategorized',
        quantity: 1 // Default quantity for booking
      }
    };

    // Calculate new products array
    const newProductsArray = [...selectedProducts, newProductSelection];
    setSelectedProducts(newProductsArray);
    
    // Load photo for this product if not already loaded
    if (!productPhotos[product.id]) {
      try {
        const photoMap = await ProductPhotoService.getPhotosByStockItemIds([product.id]);
        if (photoMap[product.id]) {
          setProductPhotos(prev => ({
            ...prev,
            [product.id]: photoMap[product.id].photo_url
          }));
        }
      } catch (error) {
        console.error('Error loading product photo:', error);
      }
    }
    
    // Close the modal
    setIsSelectInventoryItemModalOpen(false);
    
    // Save progress with new products array
    saveBookingProgress({ selectedProducts: newProductsArray });
  };

  const handleUpdateProductQuantity = async (product: any, newQuantity: number) => {
    if (!currentVehicle) {
      console.error('No current vehicle selected for product quantity update');
      return;
    }

    if (newQuantity <= 0) {
      // Remove the product if quantity is 0 or less
      const newProductsArray = selectedProducts.filter(p => p.product.id !== product.id || p.vehicle.id !== currentVehicle.id);
      setSelectedProducts(newProductsArray);
      saveBookingProgress({ selectedProducts: newProductsArray });
      return;
    }

    // Update the quantity of the existing product
    const newProductsArray = selectedProducts.map(p => {
      if (p.product.id === product.id && p.vehicle.id === currentVehicle.id) {
        return {
          ...p,
          product: {
            ...p.product,
            quantity: newQuantity
          }
        };
      }
      return p;
    });

    setSelectedProducts(newProductsArray);
    saveBookingProgress({ selectedProducts: newProductsArray });
  };

  // Component for product icon - shows photo if available, otherwise blue icon
  const ProductIcon: React.FC<{ productId: string; productName: string }> = ({ productId, productName }) => {
    const photoUrl = productPhotos[productId];
    
    if (photoUrl) {
      return (
        <div className="h-4 w-4 rounded overflow-hidden bg-gray-100 flex items-center justify-center flex-shrink-0">
          <img 
            src={photoUrl} 
            alt={productName}
            className="w-full h-full object-cover"
            onError={(e) => {
              // If image fails to load, show fallback icon
              e.currentTarget.style.display = 'none';
              const parent = e.currentTarget.parentElement;
              if (parent) {
                parent.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-green-600"><path d="M16.5 9.4 7.55 4.24"/><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.29,7 12,12 20.71,7"/><line x1="12" x2="12" y1="22" y2="12"/></svg>';
                parent.className = "h-4 w-4 rounded bg-green-100 flex items-center justify-center flex-shrink-0";
              }
            }}
          />
        </div>
      );
    }
    
    return (
      <div className="h-4 w-4 rounded bg-green-100 text-green-600 flex items-center justify-center flex-shrink-0">
        <Package2 className="h-3 w-3" />
      </div>
    );
  };

  // Simple workflow modal handlers
  const handleWorkflowSkip = () => {
    console.log('User skipped workflow assignment');
    setIsSimpleWorkflowModalOpen(false);
    setCurrentServiceForWorkflow(null);
    
    // Continue to addon check for the current service
    if (currentServiceForWorkflow) {
      checkForServiceAddons(currentServiceForWorkflow);
    }
  };

  const handleWorkflowContinue = (bookingWorkflowSteps: any[]) => {
    console.log('✅ User chose to continue with workflow assignment');
    console.log('📋 Booking workflow steps:', bookingWorkflowSteps);
    
    // Store workflow data for this service
    if (currentServiceForWorkflow && bookingWorkflowSteps && bookingWorkflowSteps.length > 0) {
      // The data is already in the correct format for booking_services.workflow_steps JSONB column
      const workflowData = bookingWorkflowSteps.map((step: any) => ({
        workflow_step_library_id: step.workflow_step_library_id,
        title: step.title,
        description: step.description,
        order_index: step.order_index,
        photos_enabled: step.photos_enabled || false,
        assigned_resources: step.assigned_resources.map((resource: any) => ({
          id: resource.id,
          name: `${resource.first_name} ${resource.last_name}`,
          avatar_url: resource.avatar_url
        }))
      }));

      // Store workflow data temporarily (will be saved when booking is created)
      setSelectedServices(prev => prev.map(service => 
        service.instanceId === currentServiceForWorkflow.instanceId 
          ? { ...service, service: { ...service.service, workflowData } }
          : service
      ));

      // Track that this service instance now has an assigned workflow
      if (currentServiceForWorkflow.instanceId) {
        setServicesWithAssignedWorkflows(prev => new Set(prev).add(currentServiceForWorkflow.instanceId!));
      }

      // Extract all unique resources from workflow steps and add to selectedResources
      const workflowResources = new Map<string, any>();
      
      bookingWorkflowSteps.forEach((step: any) => {
        step.assigned_resources.forEach((resource: any) => {
          if (!workflowResources.has(resource.id)) {
            workflowResources.set(resource.id, {
              id: resource.id,
              first_name: resource.first_name,
              last_name: resource.last_name,
              email: resource.email,
              avatar_url: resource.avatar_url
            });
          }
        });
      });

      // Add workflow resources to selectedResources (avoiding duplicates)
      setSelectedResources(prev => {
        const existingResourceIds = new Set(prev.map(r => r.id));
        const newResources = Array.from(workflowResources.values()).filter(
          resource => !existingResourceIds.has(resource.id)
        );
        
        if (newResources.length > 0) {
          console.log('🔄 Auto-adding workflow resources to booking:', newResources.map(r => `${r.first_name} ${r.last_name}`));
        }
        
        return [...prev, ...newResources];
      });

      console.log('Workflow data stored for service:', currentServiceForWorkflow.name, workflowData);
    }
    
    setIsSimpleWorkflowModalOpen(false);
    setCurrentServiceForWorkflow(null);
    
    // Continue to addon check
    if (currentServiceForWorkflow) {
      checkForServiceAddons(currentServiceForWorkflow);
    }
  };

  const checkForServiceAddons = async (service: ExtendedService) => {
    console.log('🔍 [DEBUG] checkForServiceAddons called with service:', service.name, service.id);
    try {
      const availableAddons = await getServiceAddons(service.id);
      console.log('🔍 [DEBUG] getServiceAddons returned:', availableAddons);
      
      if (availableAddons && availableAddons.length > 0) {
        console.log(`✅ Service "${service.name}" has ${availableAddons.length} addons available`);
        setCurrentService(service);
        setIsSelectAddonsModalOpen(true);
        console.log('🔍 [DEBUG] Modal state set - isSelectAddonsModalOpen: true');
      } else {
        console.log(`ℹ️ Service "${service.name}" has no addons available - opening modal anyway for testing`);
        // Temporarily open modal even if no addons for debugging
        setCurrentService(service);
        setIsSelectAddonsModalOpen(true);
        console.log('🔍 [DEBUG] Modal opened for debugging even with no addons');
      }
    } catch (error) {
      console.error('❌ Error checking for service addons:', error);
      // Open modal anyway to see if there's a different issue
      setCurrentService(service);
      setIsSelectAddonsModalOpen(true);
      console.log('🔍 [DEBUG] Modal opened despite error for debugging');
    }
  };

  const handleAddAddons = (addons: ServiceAddon[]) => {
    if (!currentService) return;
    
    // Calculate new addons object
    const newAddonsObject = {
      ...selectedServiceAddons,
      [currentService.id]: addons
    };
    setSelectedServiceAddons(newAddonsObject);
    
    // Save progress with new addons immediately - bypass timing issues
    saveBookingProgress({ selectedServiceAddons: newAddonsObject });
  };

  // Group services by vehicle
  const servicesByVehicle = selectedServices.reduce<Record<string, typeof selectedServices>>((acc, service) => {
    const vehicleId = service.vehicle.id;
    if (!acc[vehicleId]) {
      acc[vehicleId] = [];
    }
    acc[vehicleId].push(service);
    return acc;
  }, {});

  const handleSelectVehicle = (vehicle: CustomerVehicle) => {
    setCurrentVehicle(vehicle);
    setIsSelectServiceModalOpen(true);
    setIsSelectVehicleModalOpen(false);
    
    // Save progress after vehicle selection - pass vehicle directly to avoid timing issues
    saveBookingProgress({ currentVehicle: vehicle });
  };

  // Set booking type when modal opens
  useEffect(() => {
    if (isOpen) {
      console.log("Modal opened, setting initial booking type");
      // This is now a location-only booking modal
      setIsLocationBooking(true);
    }
  }, [isOpen]); // Only run when modal opens

  const handleRemoveService = (vehicleId: string, serviceIndex: number) => {
    // Get the service being removed
    const removedService = selectedServices.find((s, idx) => s.vehicle.id === vehicleId && idx === serviceIndex);
    
    // Calculate new services array
    const newServicesArray = selectedServices.filter((s, idx) => !(s.vehicle.id === vehicleId && idx === serviceIndex));
    setSelectedServices(newServicesArray);
    
    // Clean up workflow tracking for removed service instance
    if (removedService && removedService.instanceId) {
      setServicesWithWorkflows(prev => {
        const newSet = new Set(prev);
        newSet.delete(removedService.instanceId);
        return newSet;
      });
      setServicesWithAssignedWorkflows(prev => {
        const newSet = new Set(prev);
        newSet.delete(removedService.instanceId);
        return newSet;
      });
    }
    
    // Save progress with new services array immediately - bypass timing issues
    saveBookingProgress({ selectedServices: newServicesArray });
  };

  const handleRemoveAddon = (serviceId: string, addonId: string) => {
    // Calculate new addons object
    const newAddonsObject = {
      ...selectedServiceAddons,
      [serviceId]: selectedServiceAddons[serviceId]?.filter(addon => addon.id !== addonId) || []
    };
    setSelectedServiceAddons(newAddonsObject);
    
    // Save progress with new addons immediately - bypass timing issues
    saveBookingProgress({ selectedServiceAddons: newAddonsObject });
  };

  // Enhanced handlers with explicit saves on user actions
  const handleTitleChange = (newTitle: string) => {
    setTitle(newTitle);
    // Save immediately when title changes (user typed something meaningful)
    if (newTitle.trim().length > 0) {
      setTimeout(() => saveBookingProgress(), 500); // Debounce typing
    }
  };

  const handleCustomerSelect = (customer: Customer) => {
    setSelectedCustomer(customer);
    setCurrentVehicle(null);
    setIsSelectCustomerModalOpen(false);
    
    // Auto-detect customer source based on repeat status
    detectCustomerSource(customer);
    
    // Save progress after customer selection - pass customer directly to avoid timing issues
    saveBookingProgress({ selectedCustomer: customer });
  };

  const handleScheduleChange = (type: string, date: string, time: string) => {
    // Simple schedule update - no persistence needed for schedule data
    setScheduleData(prev => ({
      ...prev,
      [`${type}Date`]: date,
      [`${type}Time`]: time
    }));
  };

  const handleCustomerNotesChange = (notes: string) => {
    // Simple notes update - no persistence needed for schedule data
    setScheduleData(prev => ({
      ...prev,
      customer_notes: notes
    }));
  };

  const handleColorChange = (color: string) => {
    setBookingColor(color);
    
    // Save progress with new color immediately
    saveBookingProgress({ bookingColor: color });
  };

  const handleResourceAdd = (resource: any) => {
    // Check if resource is already selected
    if (!selectedResources.some(r => r.id === resource.id)) {
      // Calculate new resources array
      const newResourcesArray = [...selectedResources, resource];
      setSelectedResources(newResourcesArray);
      
      // Save progress with new resources immediately - bypass timing issues
      saveBookingProgress({ selectedResources: newResourcesArray });
    }
    setIsSelectResourceModalOpen(false);
  };

  const handleResourceRemove = (resourceId: string) => {
    // Simple resource removal - workflow protection will be rebuilt
    const newResourcesArray = selectedResources.filter(r => r.id !== resourceId);
    setSelectedResources(newResourcesArray);
    
    // Save progress with new resources immediately - bypass timing issues
    saveBookingProgress({ selectedResources: newResourcesArray });
  };

  if (!isOpen && !isAnimating) return null;

  // This is now a location-only booking modal
  return (
    <div 
      className={`fixed inset-0 bg-black transition-opacity duration-300 ${
        isOpen ? 'bg-opacity-50' : 'bg-opacity-0'
      } z-[10000]`}
      onClick={onClose}
      style={{
        touchAction: 'manipulation',
        WebkitTouchCallout: 'none',
        WebkitUserSelect: 'none',
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
        paddingLeft: 'env(safe-area-inset-left)',
        paddingRight: 'env(safe-area-inset-right)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
      }}
    >
      <div 
        className={`fixed inset-y-0 right-0 w-full md:w-[600px] bg-white transform transition-transform duration-300 ease-out shadow-2xl ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        onTransitionEnd={handleTransitionEnd}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <div className="h-full flex flex-col">
          {/* Header - Clean Modern Design */}
          <div className="bg-gray-900 border-b border-gray-800">
            <div className="flex items-center justify-between px-6 py-4">
              <div className="flex items-center space-x-3">
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
                <h2 className="text-xl font-semibold text-white">
                  Create Booking
                </h2>
              </div>
            </div>
          </div>

          <form 
            onSubmit={handleSubmit} 
            className="flex-1 overflow-y-auto bg-gray-50"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            <div className="space-y-0">
              {error && (
                <div className="bg-red-50 border-b border-red-200 text-red-700 px-6 py-4 text-sm">
                  <div className="flex items-center">
                    <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                    {error}
                  </div>
                </div>
              )}

              {/* Title Section */}
              <div className="bg-white border-b border-gray-200 p-6">
                <input
                  type="text"
                  value={title}
                  onChange={(e) => handleTitleChange(e.target.value)}
                  className="w-full px-4 py-3 text-base bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Enter booking title..."
                  style={{ fontSize: '16px' }}
                />
              </div>

               {/* Original Booking Number Section - Only for Warranty Bookings */}
               {isWarrantyMode && (
                 <div className="bg-blue-50 border-b border-blue-200 p-6">
                   <div className="flex items-center space-x-3">
                     <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                       <Shield className="h-4 w-4 text-blue-600" />
                     </div>
                     <div className="flex-1">
                       <h3 className="text-sm font-semibold text-blue-900 mb-1">
                         Original Booking
                       </h3>
                       <p className="text-sm text-blue-700">
                         Booking #{originalBookingNumber}
                       </p>
                     </div>
                   </div>
                 </div>
               )}



              {/* Customer Selection Section */}
              <div className={`bg-white border-b border-gray-200 p-6 transition-opacity duration-300 ${title ? 'opacity-100' : 'opacity-50 pointer-events-none'}`}>
                

                {selectedCustomer ? (
                  <div className="space-y-4">
                    <div 
                      className="flex items-center space-x-3 p-4 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setIsEditCustomerModalOpen(true);
                      }}
                    >
                      <div className="h-12 w-12 rounded-full bg-blue-500 flex items-center justify-center text-white font-medium text-sm">
                        {selectedCustomer.first_name[0]}{selectedCustomer.last_name[0]}
                      </div>
                      <div className="flex-1">
                        <div className="text-base font-medium text-gray-900">
                          {selectedCustomer.first_name} {selectedCustomer.last_name}
                        </div>
                        <div className="flex items-center space-x-4 text-sm text-gray-600">
                          <div className="flex items-center space-x-2">
                            <Mail className="h-4 w-4 text-gray-400" />
                            <span>{selectedCustomer.email}</span>
                          </div>
                          {selectedCustomer.phone && (
                            <div className="flex items-center space-x-2">
                              <Phone className="h-4 w-4 text-gray-400" />
                              <span>{selectedCustomer.phone}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Customer Source - Only show when customer is selected */}
                    {!isWarrantyMode && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Customer Source
                        </label>
                        <div className="relative">
                          <button
                            ref={customerSourceButtonRef}
                            type="button"
                            onClick={() => setIsCustomerSourceDropdownOpen(!isCustomerSourceDropdownOpen)}
                            className={`w-full flex items-center justify-between px-3 py-2 text-sm bg-white rounded-lg hover:bg-gray-50 focus:outline-none transition-colors ${
                              selectedCustomerSource 
                                ? 'border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500' 
                                : 'border-2 border-red-300 bg-red-50 focus:ring-2 focus:ring-red-500 focus:border-red-500'
                            }`}
                          >
                            <span className={selectedCustomerSource ? 'text-gray-700' : 'text-red-600'}>
                              {selectedCustomerSource ? selectedCustomerSource.name : 'Select source...'}
                            </span>
                            <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${
                              isCustomerSourceDropdownOpen ? 'rotate-180' : ''
                            } ${selectedCustomerSource ? 'text-gray-400' : 'text-red-400'}`} />
                          </button>
                          
                          {/* Customer Source Dropdown Portal */}
                          {isCustomerSourceDropdownOpen && customerSourceButtonRef.current && createPortal(
                            <>
                              <div 
                                className="fixed inset-0 z-[9999]" 
                                onClick={() => setIsCustomerSourceDropdownOpen(false)}
                              />
                              <div 
                                className="fixed z-[10000] bg-white border border-gray-300 rounded-lg shadow-xl max-h-60 overflow-y-auto"
                                style={{ 
                                  left: customerSourceButtonRef.current.getBoundingClientRect().left,
                                  top: customerSourceButtonRef.current.getBoundingClientRect().bottom + 4,
                                  width: customerSourceButtonRef.current.getBoundingClientRect().width,
                                  maxWidth: '400px'
                                }}
                              >
                                <div 
                                  onClick={() => {
                                    setSelectedCustomerSource(null);
                                    setIsCustomerSourceDropdownOpen(false);
                                  }}
                                  className="px-3 py-2 text-sm text-gray-500 hover:bg-gray-50 cursor-pointer transition-colors"
                                >
                                  No source selected
                                </div>
                                
                                {/* Hardcoded Repeat Customer Source */}
                                <div
                                  onClick={() => {
                                    setSelectedCustomerSource({ 
                                      id: 'repeat-customer', 
                                      business_id: selectedBusiness?.id || '',
                                      name: 'Repeat Customer', 
                                      is_active: true,
                                      created_at: '',
                                      updated_at: ''
                                    });
                                    setIsCustomerSourceDropdownOpen(false);
                                  }}
                                  className="px-3 py-2 text-sm text-gray-900 hover:bg-gray-50 cursor-pointer transition-colors border-t border-gray-200 flex items-center justify-between"
                                >
                                  <span>Repeat Customer</span>
                                  <span className="text-xs text-green-600 font-medium">System</span>
                                </div>
                                
                                {/* Regular Customer Sources */}
                                {(() => {
                                  console.log('🎨 [CreateBookingModal] Rendering customer sources dropdown:', {
                                    customerSourcesLength: customerSources.length,
                                    customerSources: customerSources.map(s => ({ id: s.id, name: s.name }))
                                  });
                                  // Filter out "Warranty" sources for regular bookings
                                  const filteredSources = customerSources.filter(source => source.name.toLowerCase() !== 'warranty');
                                  return filteredSources.map((source) => (
                                    <div
                                      key={source.id}
                                      onClick={() => {
                                        console.log('📱 [CreateBookingModal] Customer source selected:', source);
                                        setSelectedCustomerSource(source);
                                        setIsCustomerSourceDropdownOpen(false);
                                      }}
                                      className="px-3 py-2 text-sm text-gray-900 hover:bg-gray-50 cursor-pointer transition-colors border-t border-gray-200"
                                    >
                                      {source.name}
                                    </div>
                                  ));
                                })()}
                                
                                {/* Add Source Option */}
                                <div
                                  onClick={() => {
                                    setIsCustomerSourceDropdownOpen(false);
                                    setIsCreateSourceModalOpen(true);
                                  }}
                                  className="px-3 py-2 text-sm text-blue-600 hover:bg-gray-50 cursor-pointer transition-colors border-t border-gray-200 flex items-center space-x-2"
                                >
                                  <Plus className="h-4 w-4" />
                                  <span>Add Source</span>
                                </div>
                              </div>
                            </>,
                            document.body
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setIsSelectCustomerModalOpen(true);
                    }}
                    className="w-full px-4 py-4 border-2 border-dashed border-blue-300 rounded-lg text-sm text-blue-600 hover:text-blue-800 hover:border-blue-400 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                  >
                    Select Customer
                  </button>
                )}

                {/* Booking Notes - Only show when customer is selected */}
                {selectedCustomer && (
                  <div className="mt-4">
                    <textarea
                      value={scheduleData.customer_notes || ''}
                      onChange={(e) => handleCustomerNotesChange(e.target.value)}
                      placeholder="Add booking notes..."
                      className="w-full h-24 px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
                    />
                  </div>
                )}
              </div>

              {/* Services Section */}
              {selectedCustomer && (
                <div className="bg-white">
                  {/* Vehicle Selection */}
                  <div className="space-y-0">
                    {/* Display vehicles with their services */}
                    {Object.entries(servicesByVehicle).map(([vehicleId, services]) => {
                      const vehicle = services[0].vehicle;
                      return (
                        <div key={vehicleId} className="bg-white border-b border-gray-200">
                          <div className="p-6">
                            <div 
                              className="flex items-center p-4 bg-gray-900 rounded-lg cursor-pointer hover:bg-gray-800 transition-colors"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedVehicleToEdit(vehicle);
                                setIsEditVehicleModalOpen(true);
                              }}
                            >
                              <div>
                                <div className="text-base font-medium text-white">
                                  {vehicle.year} {vehicle.make} {vehicle.model}
                                </div>
                                {vehicle.color && (
                                  <div className="text-sm text-gray-300">{vehicle.color}</div>
                                )}
                              </div>
                            </div>

                            {/* Services & Products Section Header */}
                            <div className="mt-6 mb-4 flex items-center justify-between">
                              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide">SERVICES & PRODUCTS</h3>
                              <div className="text-sm font-medium text-gray-500 uppercase tracking-wide">TOTAL</div>
                            </div>

                            {/* Services and Products for this vehicle */}
                            <div className="space-y-4">
                              {services.map((service, index) => (
                                <div key={index} className="border-b border-gray-100 pb-4 last:border-b-0">
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <div className="text-base font-medium text-gray-900">
                                        {service.service.name}
                                      </div>
                                      <div className="mt-2 flex items-center space-x-3">
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedServiceToEdit(service);
                                            setIsEditServiceModalOpen(true);
                                          }}
                                          className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                                        >
                                          Edit
                                        </button>
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            checkForServiceAddons(service.service);
                                          }}
                                          className="text-sm text-green-600 hover:text-green-800 font-medium"
                                        >
                                          Add-ons
                                        </button>
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedServices(prev => prev.filter(s => s !== service));
                                          }}
                                          className="text-sm text-red-600 hover:text-red-800 font-medium"
                                        >
                                          Remove
                                        </button>
                                        {servicesWithWorkflows.has(service.instanceId) && (
                                          <button
                                            type="button"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setCurrentServiceForWorkflow(service.service);
                                              setIsSimpleWorkflowModalOpen(true);
                                            }}
                                            className={`text-sm font-medium transition-colors ${
                                              service.service.workflowData && service.service.workflowData.length > 0
                                                ? 'text-green-600 hover:text-green-800'
                                                : 'text-blue-600 hover:text-blue-800'
                                            }`}
                                          >
                                            {service.service.workflowData && service.service.workflowData.length > 0 
                                              ? 'Workflow' 
                                              : 'Assign Workflow'
                                            }
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                    <div className="text-right ml-4">
                                      <div className="text-lg font-semibold text-gray-900">
                                        ${service.priceOption.price.toLocaleString()}
                                      </div>
                                      <div className="mt-1 text-sm text-gray-500">
                                        {service.priceOption.duration_minutes >= 1440 
                                          ? `${Math.round(service.priceOption.duration_minutes / 1440)} days`
                                          : `${Math.round(service.priceOption.duration_minutes / 60)} hours`
                                        }
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}

                              {/* Products for this vehicle */}
                              {selectedProducts.filter(p => p.vehicle.id === vehicle.id).map((productSelection) => (
                                <div key={productSelection.instanceId} className="border-b border-gray-100 pb-4 last:border-b-0">
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <div className="flex items-center space-x-2">
                                        <ProductIcon productId={productSelection.product.id} productName={productSelection.product.name} />
                                        <div className="text-base font-medium text-gray-900">
                                          {productSelection.product.name}
                                        </div>
                                      </div>
                                      <div className="text-sm text-gray-600 mt-1">{productSelection.product.category_name}</div>
                                      <div className="mt-2 flex items-center space-x-3">
                                        <button
                                          type="button"
                                          onClick={() => {
                                            const newProducts = selectedProducts.filter(p => p.instanceId !== productSelection.instanceId);
                                            setSelectedProducts(newProducts);
                                            saveBookingProgress({ selectedProducts: newProducts });
                                          }}
                                          className="text-sm text-red-600 hover:text-red-800 font-medium"
                                        >
                                          Remove
                                        </button>
                                      </div>
                                    </div>
                                    <div className="text-right">
                                      <div className="text-lg font-semibold text-green-600">
                                        ${(productSelection.product.price * productSelection.product.quantity).toFixed(2)}
                                      </div>
                                      <div className="text-sm text-gray-500">
                                        Qty: {productSelection.product.quantity}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>

                            {/* Add Service Button */}
                            <div className="mt-6">
                              <button
                                type="button"
                                onClick={() => {
                                  setCurrentVehicle(vehicle);
                                  setIsSelectServiceModalOpen(true);
                                }}
                                className="w-full px-4 py-4 border-2 border-dashed border-blue-300 rounded-lg text-sm text-blue-600 hover:text-blue-800 hover:border-blue-400 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                              >
                                Add Service
                              </button>
                            </div>

                            {/* Add Product Button */}
                            <div className="mt-3">
                              <button
                                type="button"
                                onClick={() => {
                                  setIsSelectInventoryItemModalOpen(true);
                                }}
                                className="w-full px-4 py-4 border-2 border-dashed border-green-300 rounded-lg text-sm text-green-600 hover:text-green-800 hover:border-green-400 hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors"
                              >
                                Add Product
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {/* Add new vehicle button */}
                    {selectedServices.length === 0 ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setIsSelectVehicleModalOpen(true);
                        }}
                        className="w-full px-4 py-4 border-2 border-dashed border-blue-300 rounded-lg text-sm text-blue-600 hover:text-blue-800 hover:border-blue-400 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                      >
                        Select Vehicle
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setIsSelectVehicleModalOpen(true);
                        }}
                        className="w-full px-4 py-4 border-2 border-dashed border-blue-300 rounded-lg text-sm text-blue-600 hover:text-blue-800 hover:border-blue-400 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                      >
                        Add Another Vehicle
                      </button>
                    )}
                  </div>
                </div>
              )}

                
              {/* Pricing Summary */}
              {(selectedServices.length > 0 || selectedProducts.length > 0) && (
                <div className="bg-white border-b border-gray-200 p-6">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">Pricing Summary</h3>
                  <div className="space-y-1">
                    <div className="flex justify-between items-center py-1">
                      <span className="flex items-center text-sm text-gray-600">
                        <Clock className="w-3 h-3 mr-1.5 text-gray-400" />
                        Total Duration
                      </span>
                      <span className="text-sm font-medium text-gray-900">
                        {(() => {
                          const totalMinutes = selectedServices.reduce((sum, s) => {
                            // Add service duration
                            let total = sum + s.priceOption.duration_minutes;
                            // Add addon durations for this service
                            const addons = selectedServiceAddons[s.service.id] || [];
                            total += addons.reduce((addonSum, addon) => addonSum + addon.duration_minutes, 0);
                            return total;
                          }, 0);
                          return totalMinutes >= 1440 
                            ? `${Math.round(totalMinutes / 1440)} days`
                            : `${Math.round(totalMinutes / 60)} hours`;
                        })()}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-1">
                      <span className="text-sm text-gray-600">Subtotal</span>
                      <span className="text-sm font-medium text-gray-900">
                        ${(() => {
                          // Calculate services subtotal
                          const servicesTotal = selectedServices.reduce((sum, s) => {
                            // Add service price
                            let total = sum + s.priceOption.price;
                            // Add addon prices for this service
                            const addons = selectedServiceAddons[s.service.id] || [];
                            total += addons.reduce((addonSum, addon) => addonSum + addon.price, 0);
                            return total;
                          }, 0);
                          
                          // Calculate products subtotal
                          const productsTotal = selectedProducts.reduce((sum, p) => {
                            return sum + (p.product.price * p.product.quantity);
                          }, 0);
                          
                          return (servicesTotal + productsTotal).toLocaleString();
                        })()}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-1">
                      <span className="text-sm text-gray-600">GST (5%)</span>
                      <span className="text-sm font-medium text-gray-900">
                        + ${(() => {
                          // Calculate GST on services
                          const servicesGST = selectedServices.reduce((sum, s) => {
                            let total = s.priceOption.price;
                            // Add addon prices for this service
                            const addons = selectedServiceAddons[s.service.id] || [];
                            total += addons.reduce((addonSum, addon) => addonSum + addon.price, 0);
                            // Only apply tax if service is not tax exempt
                            return s.service.tax_exempt ? sum : sum + (total * 0.05);
                          }, 0);
                          
                          // Calculate GST on products (assuming products are taxable)
                          const productsGST = selectedProducts.reduce((sum, p) => {
                            return sum + ((p.product.price * p.product.quantity) * 0.05);
                          }, 0);
                          
                          return (servicesGST + productsGST).toLocaleString();
                        })()}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-1.5 border-t border-gray-200">
                      <span className="text-sm font-semibold text-gray-900">Total</span>
                      <span className="text-sm font-bold text-blue-600">
                        ${(() => {
                          // Calculate services total with tax
                          const servicesTotal = selectedServices.reduce((sum, s) => {
                            let total = s.priceOption.price;
                            // Add addon prices for this service
                            const addons = selectedServiceAddons[s.service.id] || [];
                            total += addons.reduce((addonSum, addon) => addonSum + addon.price, 0);
                            // Add tax if not exempt
                            if (!s.service.tax_exempt) {
                              total += total * 0.05;
                            }
                            return sum + total;
                          }, 0);
                          
                          // Calculate products total with tax
                          const productsTotal = selectedProducts.reduce((sum, p) => {
                            const subtotal = p.product.price * p.product.quantity;
                            const withTax = subtotal + (subtotal * 0.05); // Assuming products are taxable
                            return sum + withTax;
                          }, 0);
                          
                          return (servicesTotal + productsTotal).toLocaleString();
                        })()}
                      </span>
                    </div>
                  </div>
                </div>
              )}



                {/* Scheduling Section */}
                {selectedServices.length > 0 && (
                  <div className="bg-white border-b border-gray-200 p-6">
                    <CreateScheduleEditor
                      dropOffDate={scheduleData.dropOffDate}
                      dropOffTime={scheduleData.dropOffTime}
                      startDate={scheduleData.startDate}
                      startTime={scheduleData.startTime}
                      endDate={scheduleData.endDate}
                      endTime={scheduleData.endTime}
                      serviceDuration={totalDurationMinutes}
                      onDateTimeChange={(type, date, time) => {
                        handleScheduleChange(type, date, time);
                      }}
                      disabled={loading}
                    />
                  </div>
                )}
                
                {/* Resource Selection */}
                {selectedServices.length > 0 && (
                  <div className="bg-white border-b border-gray-200 p-6">
                    <button
                      type="button"
                      onClick={() => setIsResourcesExpanded(!isResourcesExpanded)}
                      className="w-full flex items-center justify-end mb-4"
                    >
                      <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${isResourcesExpanded ? 'rotate-180' : ''}`} />
                    </button>
                    
                    {/* Collapsed State - Show resource icons and add button */}
                    {!isResourcesExpanded && (
                      <div className="mb-2">
                        {selectedResources.length > 0 && (
                          <div className="flex items-center space-x-2 mb-2">
                            {selectedResources.map((resource) => (
                              <div key={resource.id} className="relative group">
                                <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center shadow-sm overflow-hidden">
                                  {resource.avatar_url ? (
                                    <img 
                                      src={resource.avatar_url} 
                                      alt={`${resource.first_name} ${resource.last_name}`}
                                      className="w-full h-full object-cover"
                                    />
                                  ) : (
                                    <span className="text-xs font-bold text-white">
                                      {resource.first_name[0]}{resource.last_name[0]}
                                    </span>
                                  )}
                                </div>
                                {/* Tooltip */}
                                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                                  {resource.first_name} {resource.last_name}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {/* Add Resource Button in Collapsed State */}
                        <button
                          type="button"
                          onClick={() => setIsSelectResourceModalOpen(true)}
                          className="w-full px-2 py-1.5 border-2 border-dashed border-blue-300 rounded-lg text-xs font-medium text-blue-600 hover:text-blue-800 hover:border-blue-400 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                          style={{ minHeight: '32px' }}
                        >
                          {selectedResources.length === 0 ? 'Add Resources' : 'Add Another Resource'}
                        </button>
                      </div>
                    )}
                    
                    {/* Expanded State - Show full resource details */}
                    {isResourcesExpanded && (
                      <>
                        {selectedResources.length > 0 ? (
                      <div className="space-y-3">
                        {selectedResources.map((resource) => (
                          <div key={resource.id} className="flex items-center justify-between bg-gray-50 rounded-lg p-3 border border-gray-200">
                            <div className="flex items-center space-x-3">
                                <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center overflow-hidden">
                                {resource.avatar_url ? (
                                  <img 
                                    src={resource.avatar_url} 
                                    alt={`${resource.first_name} ${resource.last_name}`}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <span className="text-sm font-medium text-white">
                                    {resource.first_name[0]}{resource.last_name[0]}
                                  </span>
                                )}
                              </div>
                              <div>
                                <div className="text-sm font-semibold text-gray-900">
                                  {resource.first_name} {resource.last_name}
                                </div>
                                <div className="text-sm text-gray-600">
                                  {resource.email}
                                </div>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleResourceRemove(resource.id)}
                              className="text-sm font-medium text-red-600 hover:text-red-800 px-2 py-1 rounded-lg hover:bg-red-50 transition-colors"
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={() => setIsSelectResourceModalOpen(true)}
                          className="w-full mt-2 px-3 py-3 border-2 border-dashed border-blue-300 rounded-lg text-sm font-medium text-blue-600 hover:text-blue-800 hover:border-blue-400 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                        >
                          Add Another Resource
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setIsSelectResourceModalOpen(true)}
                        className="w-full px-3 py-3 border-2 border-dashed border-blue-300 rounded-lg text-sm font-medium text-blue-600 hover:text-blue-800 hover:border-blue-400 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                        style={{ minHeight: '48px' }}
                      >
                        Add Resources
                      </button>
                    )}
                      </>
                    )}
                  </div>
                )}

                {/* Color Picker */}
                {selectedServices.length > 0 && (
                  <div className="bg-white border-b border-gray-200 p-4">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">Booking Colour</h3>
                    <div className="grid grid-cols-5 gap-2">
                      {/* Current Color */}
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedColorMethod('custom');
                          setIsColorPickerModalOpen(true);
                        }}
                        className={`flex flex-col items-center justify-center p-2 border-2 rounded-lg transition-colors ${
                          selectedColorMethod === 'custom' 
                            ? 'border-blue-500 bg-blue-50' 
                            : 'border-gray-200 hover:border-gray-300 opacity-60 hover:opacity-80'
                        }`}
                      >
                        <div className="w-8 h-8 rounded-full mb-1 overflow-hidden">
                          {bookingSecondaryColor ? (
                            <div className="w-full h-full flex">
                              <div 
                                className="w-1/2 h-full"
                                style={{ backgroundColor: bookingColor }}
                              />
                              <div 
                                className="w-1/2 h-full"
                                style={{ backgroundColor: bookingSecondaryColor }}
                              />
                            </div>
                          ) : (
                            <div 
                              className="w-full h-full"
                              style={{ backgroundColor: bookingColor }}
                            />
                          )}
                        </div>
                        <span className="text-xs font-medium text-gray-900">Current</span>
                      </button>

                      {/* Color Presets */}
                      {colorPresets.map((preset) => (
                        <div key={preset.id} className="relative">
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedColorMethod(preset.id);
                              setBookingColor(preset.primary_color);
                              setBookingSecondaryColor(preset.secondary_color || null);
                              saveBookingProgress({ 
                                bookingColor: preset.primary_color, 
                                bookingSecondaryColor: preset.secondary_color 
                              });
                            }}
                            className={`flex flex-col items-center justify-center p-2 border-2 rounded-lg transition-colors w-full ${
                              selectedColorMethod === preset.id 
                                ? 'border-blue-500 bg-blue-50' 
                                : 'border-gray-200 hover:border-gray-300 opacity-60 hover:opacity-80'
                            }`}
                          >
                            <div className="w-8 h-8 rounded-full mb-1 overflow-hidden">
                              {preset.secondary_color ? (
                                <div className="w-full h-full flex">
                                  <div 
                                    className="w-1/2 h-full"
                                    style={{ backgroundColor: preset.primary_color }}
                                  />
                                  <div 
                                    className="w-1/2 h-full"
                                    style={{ backgroundColor: preset.secondary_color }}
                                  />
                                </div>
                              ) : (
                                <div 
                                  className="w-full h-full"
                                  style={{ backgroundColor: preset.primary_color }}
                                />
                              )}
                            </div>
                            <span className="text-xs font-medium text-gray-900">{preset.title}</span>
                          </button>
                          <button
                            type="button"
                            onClick={async (e) => {
                              e.stopPropagation();
                              
                              const confirmed = window.confirm(
                                `Are you sure you want to delete the "${preset.title}" color preset? This action cannot be undone.`
                              );
                              
                              if (!confirmed) return;
                              
                              try {
                                await deleteColorPreset(preset.id);
                                setColorPresets(prev => prev.filter(p => p.id !== preset.id));
                                if (selectedColorMethod === preset.id) {
                                  setSelectedColorMethod('custom');
                                }
                              } catch (error) {
                                console.error('Error deleting color preset:', error);
                              }
                            }}
                            className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center text-xs font-medium shadow-sm transition-colors"
                          >
                            ×
                          </button>
                        </div>
                      ))}

                      {/* Add Preset Button */}
                      <button
                        type="button"
                        onClick={() => setIsCreateColorPresetModalOpen(true)}
                        className="flex flex-col items-center justify-center p-2 border-2 border-dashed border-gray-300 rounded-lg hover:border-gray-400 hover:bg-gray-50 transition-colors opacity-60 hover:opacity-80"
                      >
                        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center mb-1">
                          <Plus className="w-4 h-4 text-gray-500" />
                        </div>
                        <span className="text-xs font-medium text-gray-600">Add</span>
                      </button>
                    </div>
                  </div>
                )}

                {/* Submit Button */}
                <div className="bg-white p-6">
                  <button
                    type="submit"
                    disabled={loading || !title || !selectedCustomer || selectedServices.length === 0}
                    className={`w-full px-4 py-3 text-white font-semibold rounded-lg transition-colors shadow-sm ${
                      (title && selectedCustomer && selectedServices.length > 0) 
                        ? 'bg-blue-600 hover:bg-blue-700' 
                        : 'bg-gray-300 cursor-not-allowed'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {loading ? (
                      <div className="flex items-center justify-center">
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                        Creating Booking...
                      </div>
                    ) : (
                      'Create Booking'
                    )}
                  </button>
                </div>
              </div>
          </form>
        </div>
      </div>

      <SelectCustomerModal
        isOpen={isSelectCustomerModalOpen}
        onClose={() => setIsSelectCustomerModalOpen(false)}
        onSelect={handleCustomerSelect}
      />

      <SelectVehicleModal
        isOpen={isSelectVehicleModalOpen}
        onClose={() => setIsSelectVehicleModalOpen(false)}
        onSelect={handleSelectVehicle}
        vehicles={selectedCustomer?.vehicles || []}
        selectedVehicles={selectedServices.map(s => s.vehicle)}
        onAddVehicle={() => {
          setIsSelectVehicleModalOpen(false);
          setIsCreateVehicleModalOpen(true);
        }}
      />

      <CreateVehicleModal
        isOpen={isCreateVehicleModalOpen}
        onClose={() => setIsCreateVehicleModalOpen(false)}
        onSuccess={handleVehicleCreated}
        customerId={selectedCustomer?.id || ''}
      />

      <EditCustomerModal
        isOpen={isEditCustomerModalOpen}
        onClose={() => setIsEditCustomerModalOpen(false)}
        onSuccess={(updatedCustomer) => {
          setIsEditCustomerModalOpen(false);
          setSelectedCustomer(updatedCustomer);
        }}
        customer={selectedCustomer}
      />

      <SelectServiceModal
        isOpen={isSelectServiceModalOpen}
        onClose={() => setIsSelectServiceModalOpen(false)}
        onAdd={async (service, priceOption) => {
          handleAddService(service, priceOption);
          return Promise.resolve();
        }}
      />

      <SelectInventoryItemModal
        isOpen={isSelectInventoryItemModalOpen}
        onClose={() => setIsSelectInventoryItemModalOpen(false)}
        onAdd={async (product) => {
          handleAddProduct(product);
          return Promise.resolve();
        }}
        onUpdateQuantity={async (product, newQuantity) => {
          handleUpdateProductQuantity(product, newQuantity);
          return Promise.resolve();
        }}
        currentBookingProducts={selectedProducts}
      />

      <SelectResourceModal
        isOpen={isSelectResourceModalOpen}
        onClose={() => setIsSelectResourceModalOpen(false)}
        onSelect={handleResourceAdd}
        selectedResources={selectedResources}
      />

      <EditVehicleModal
        isOpen={isEditVehicleModalOpen}
        onClose={() => {
          setIsEditVehicleModalOpen(false);
          setSelectedVehicleToEdit(null);
        }}
        onUpdate={(updatedVehicle: CustomerVehicle) => {
          // Update the vehicle in the selectedServices array
          setSelectedServices(prev => prev.map(s => 
            s.vehicle.id === updatedVehicle.id
              ? { ...s, vehicle: updatedVehicle }
              : s
          ));
          setIsEditVehicleModalOpen(false);
          setSelectedVehicleToEdit(null);
        }}
        vehicle={selectedVehicleToEdit}
      />

      <EditServiceModal
        isOpen={isEditServiceModalOpen}
        onClose={() => {
          setIsEditServiceModalOpen(false);
          setSelectedServiceToEdit(null);
        }}
        onSave={async (updatedService) => {
          // Update the service in the selectedServices array
          setSelectedServices(prev => prev.map(s => 
            s === selectedServiceToEdit
              ? {
                  ...s,
                  service: {
                    ...s.service,
                    name: updatedService.name,
                    description: updatedService.description
                  },
                  priceOption: {
                    ...s.priceOption,
                    price: updatedService.price,
                    duration_minutes: updatedService.duration_minutes
                  }
                }
              : s
          ));
          setIsEditServiceModalOpen(false);
          setSelectedServiceToEdit(null);
        }}
        service={selectedServiceToEdit ? {
          name: selectedServiceToEdit.service.name,
          description: selectedServiceToEdit.service.description,
          price: selectedServiceToEdit.priceOption.price,
          duration_minutes: selectedServiceToEdit.priceOption.duration_minutes,
          is_active: true,
          tax_rate: selectedServiceToEdit.service.tax_rate || 5,
          tax_exempt: selectedServiceToEdit.service.tax_exempt || false,
          addons: selectedServiceAddons[selectedServiceToEdit.service.id] || []
        } : null}
      />

      <SelectServiceAddonsModal
        isOpen={isSelectAddonsModalOpen}
        onClose={() => {
          setIsSelectAddonsModalOpen(false);
          setCurrentService(null);
        }}
        serviceId={currentService?.id || ''}
        onSelect={handleAddAddons}
        selectedAddons={currentService ? selectedServiceAddons[currentService.id] || [] : []}
      />

      <ColorPickerModal
        isOpen={isColorPickerModalOpen}
        onClose={() => setIsColorPickerModalOpen(false)}
        onColorSelect={(color, secondaryColor) => {
          setSelectedColorMethod('custom');
          setBookingColor(color);
          setBookingSecondaryColor(secondaryColor || null);
          saveBookingProgress({ bookingColor: color, bookingSecondaryColor: secondaryColor });
          setIsColorPickerModalOpen(false);
        }}
        currentColor={bookingColor}
        currentSecondaryColor={bookingSecondaryColor || undefined}
      />

      <CreateColorPresetModal
        isOpen={isCreateColorPresetModalOpen}
        onClose={() => setIsCreateColorPresetModalOpen(false)}
        onSave={async (title, primaryColor, secondaryColor) => {
          if (!selectedBusiness?.id) return;
          
          try {
            const newPreset = await createColorPreset({
              business_id: selectedBusiness.id,
              title,
              primary_color: primaryColor,
              secondary_color: secondaryColor
            });
            setColorPresets(prev => [...prev, newPreset]);
            setIsCreateColorPresetModalOpen(false);
          } catch (error) {
            console.error('Error creating color preset:', error);
          }
        }}
      />

      {/* Create Customer Source Modal */}
      {isCreateSourceModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10001]">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Add Customer Source
              </h3>
              <button
                onClick={() => {
                  setIsCreateSourceModalOpen(false);
                  setNewSourceName('');
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Source Name
              </label>
              <input
                type="text"
                value={newSourceName}
                onChange={(e) => setNewSourceName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="Enter source name (e.g., Google Ads, Referral)"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newSourceName.trim()) {
                    handleCreateCustomerSource();
                  }
                }}
                autoFocus
              />
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => {
                  setIsCreateSourceModalOpen(false);
                  setNewSourceName('');
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                disabled={createSourceLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreateCustomerSource}
                disabled={!newSourceName.trim() || createSourceLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {createSourceLoading ? 'Creating...' : 'Create Source'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Simple Workflow Modal */}
      <SimpleWorkflowModal
        isOpen={isSimpleWorkflowModalOpen}
        onClose={() => {
          setIsSimpleWorkflowModalOpen(false);
          setCurrentServiceForWorkflow(null);
        }}
        onSkip={handleWorkflowSkip}
        onContinue={handleWorkflowContinue}
        serviceName={currentServiceForWorkflow?.name || ''}
        serviceId={currentServiceForWorkflow?.id || ''}
        isEditing={(() => {
          // Check if this service instance already has workflow data stored
          if (!currentServiceForWorkflow?.instanceId) return false;
          const existingService = selectedServices.find(s => s.instanceId === currentServiceForWorkflow.instanceId);
          return !!(existingService?.service.workflowData && existingService.service.workflowData.length > 0);
        })()}
        existingWorkflowData={(() => {
          // Get existing workflow data for this service instance
          if (!currentServiceForWorkflow?.instanceId) return [];
          const existingService = selectedServices.find(s => s.instanceId === currentServiceForWorkflow.instanceId);
          if (existingService?.service.workflowData) {
            // Convert from stored format to BookingWorkflowStep format
            return existingService.service.workflowData.map((step: any) => ({
              workflow_step_library_id: step.workflow_step_library_id,
              title: step.title,
              description: step.description || '',
              order_index: step.order_index,
              photos_enabled: step.photos_enabled || false,
              assigned_resources: step.assigned_resources.map((resource: any) => ({
                id: resource.id,
                first_name: resource.name.split(' ')[0] || '',
                last_name: resource.name.split(' ').slice(1).join(' ') || '',
                email: resource.email || '',
                avatar_url: resource.avatar_url
              }))
            }));
          }
          return [];
        })()}
      />
    </div>
  );
}